// src/pages/Pricing/PricingPage.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import "./PricingPage.css";
import { createCheckoutSession, openBillingPortal } from "../../api/pricingApi";
import { ArrowUpRight } from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { PAID_PLAN_CODES, PLAN_CODES, isStarterPlanCode } from "../../utils/billingPlans";
import {
  clearBillingIntent,
  readBillingIntent,
  storeBillingIntent,
} from "../../utils/billingIntent";

export default function PricingPage() {
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState("");

  const user = useSelector((s) => s.user?.data); // logged-in check
  const navigate = useNavigate();
  const location = useLocation();

  // prevent double-trigger across renders
  const resumedRef = useRef(false);
  const manageBillingRef = useRef(false);
  const billingPlanCode = user?.billing?.plan?.code || null;
  const billingPlanName = user?.billing?.plan?.name || "";
  const billingStatus = user?.billing?.subscription?.status || null;
  const billingProvider = user?.billing?.subscription?.provider || null;
  const externalProviderName =
    billingProvider === "app_store"
      ? "App Store"
      : billingProvider === "play_store"
        ? "Google Play"
        : null;
  const isCurrentStarterPlan = isStarterPlanCode(billingPlanCode, billingPlanName);
  const hasManagedSubscription = useMemo(
    () => ["incomplete", "trialing", "active", "past_due", "unpaid", "paused"].includes(billingStatus),
    [billingStatus]
  );
  const hasManagedPaidSubscription =
    hasManagedSubscription && PAID_PLAN_CODES.has(billingPlanCode);
  const finishBillingIntent = useCallback(() => {
    clearBillingIntent();
    if (location.state?.intent) {
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  const plans = useMemo(
    () => ({
      starter: {
        title: "Starter",
        subtitle: "Free plan for light usage.",
        badge: "",
        price: "Free",
        cadence: "",
        items: [
          "15 total minutes of practice time",
          "Practice sessions",
          "Upgrade anytime",
        ],
        cta: "Get Starter",
        planCode: PLAN_CODES.starter,
        unitType: "flat",
      },
      pro: {
        title: "Pro",
        subtitle: "Steady daily practice.",
        badge: "",
        price: "$12.99",
        cadence: "/month",
        items: [
          "30 minutes per day",
          "Practice conversations",
          "Training plans",
  
        ],
        cta: "Get Pro",
        planCode: PLAN_CODES.pro,
        unitType: "flat",
      },
      elite: {
        title: "Elite",
        subtitle: { badge: "Unlimited", suffix: " preparation." },
        badge: "Recommended",
        price: "$24.99",
        cadence: "/month",
        items: [
          { strong: "Everything in Pro" },
          { strong: "Unlimited", suffix: " practice" },
        
        ],
        cta: "Get Elite",
        planCode: PLAN_CODES.elite,
        unitType: "flat",
      },
    }),
    []
  );

  const ctaForPlan = useCallback(
    (targetPlanCode) => {
      if (!hasManagedSubscription) return undefined;
      if (billingPlanCode === targetPlanCode) return undefined;
      if (isCurrentStarterPlan) {
        if (targetPlanCode === PLAN_CODES.pro) return "Upgrade to Pro";
        if (targetPlanCode === PLAN_CODES.elite) return "Upgrade to Elite";
        return undefined;
      }
      if (targetPlanCode === PLAN_CODES.starter) return "Manage subscription";
      if (billingPlanCode === PLAN_CODES.pro && targetPlanCode === PLAN_CODES.elite) {
        return "Upgrade to Elite";
      }
      return "Change plan";
    },
    [billingPlanCode, hasManagedSubscription, isCurrentStarterPlan]
  );

  const startCheckout = useCallback(
    async ({ planCode, quantity }) => {
      // If not signed in, save intent and send to /auth.
      if (!user?.id) {
        const intent = storeBillingIntent({
          from: "/pricing",
          intent: "checkout",
          planCode,
          quantity: quantity ?? null,
        });
        navigate("/auth", { state: intent });
        return;
      }

      setError("");
      setLoadingPlan(planCode);

      try {
        if (hasManagedPaidSubscription && externalProviderName) {
          finishBillingIntent();
          setError(`Your active subscription is managed through ${externalProviderName}. Use ${externalProviderName} to change or cancel your plan.`);
          setLoadingPlan(null);
          return;
        }

        if (hasManagedSubscription) {
          const isStarterToPaidUpgradeByCheckout =
            isCurrentStarterPlan && PAID_PLAN_CODES.has(planCode);
          const shouldUsePortal =
            !isCurrentStarterPlan && billingPlanCode !== planCode;

          if (billingPlanCode === planCode) {
            finishBillingIntent();
            setError("You are already on this plan.");
            setLoadingPlan(null);
            return;
          }
          if (shouldUsePortal) {
            const portal = await openBillingPortal();
            if (!portal?.url) throw new Error("No billing portal URL returned.");
            finishBillingIntent();
            window.location.assign(portal.url);
            return;
          }

          if (!isStarterToPaidUpgradeByCheckout) {
            finishBillingIntent();
            setError("Use the billing portal to change your subscription.");
            setLoadingPlan(null);
            return;
          }
        }

        const data = await createCheckoutSession({ planCode, quantity });
        if (!data?.url) throw new Error("No checkout URL returned.");
        finishBillingIntent();
        window.location.assign(data.url);
      } catch (e) {
        if (e?.response?.status === 401) {
          const intent = storeBillingIntent({
            from: "/pricing",
            intent: "checkout",
            planCode,
            quantity: quantity ?? null,
          });
          navigate("/auth", { replace: true, state: intent });
          return;
        }

        finishBillingIntent();
        const apiError = e?.response?.data?.error;
        setError(apiError || e?.message || "Failed to start checkout.");
        setLoadingPlan(null);
      }
    },
    [billingPlanCode, externalProviderName, finishBillingIntent, hasManagedPaidSubscription, hasManagedSubscription, isCurrentStarterPlan, navigate, user?.id]
  );

  useEffect(() => {
    if (manageBillingRef.current) return;

    const search = new URLSearchParams(location.search);
    const wantsManageBilling = search.get("intent") === "manage-billing";
    const st = readBillingIntent(location.state);
    const hasManageBillingIntent = wantsManageBilling || st?.intent === "manageBilling";
    if (!hasManageBillingIntent) return;

    if (!user?.id) {
      const intent = storeBillingIntent({
        from: "/pricing?intent=manage-billing",
        intent: "manageBilling",
      });
      navigate("/auth", { state: intent });
      return;
    }

    manageBillingRef.current = true;

    (async () => {
      try {
        const portal = await openBillingPortal();
        if (!portal?.url) throw new Error("No billing portal URL returned.");
        finishBillingIntent();
        window.location.assign(portal.url);
      } catch (e) {
        if (e?.response?.status === 401) {
          const intent = storeBillingIntent({
            from: "/pricing?intent=manage-billing",
            intent: "manageBilling",
          });
          navigate("/auth", { replace: true, state: intent });
          return;
        }

        finishBillingIntent();
        const apiError = e?.response?.data?.error;
        setError(apiError || e?.message || "Could not open billing portal.");
      }
    })();
  }, [finishBillingIntent, location.search, location.state, navigate, user?.id]);

  useEffect(() => {
    if (resumedRef.current) return;

    const st = readBillingIntent(location.state);
    if (st?.intent !== "checkout" || !st?.planCode) return;

    if (!user?.id) {
      navigate("/auth", { replace: true, state: st });
      return;
    }

    resumedRef.current = true;
    startCheckout({ planCode: st.planCode, quantity: st.quantity ?? null });
  }, [location.state, navigate, startCheckout, user?.id]);

  return (
    <div className="pricing-page">
      <main className="pricing-main">
        {error ? (
          <div className="pricing-error" role="alert">
            {error}
          </div>
        ) : null}
        <div className="pricing-header-top">

            <h1 className="pricing-h1"> Plans</h1>
            <p className="pricing-subhead">Choose a plan that fits your goals.</p>
        </div>

        <section className="pricing-grid" aria-label="Pricing plans">
          <PricingCard
            {...plans.starter}
            active={false}
            isCurrentPlan={hasManagedSubscription && billingPlanCode === plans.starter.planCode}
            ctaOverride={ctaForPlan(plans.starter.planCode)}
            loading={loadingPlan === plans.starter.planCode}
            onSelect={() => startCheckout({ planCode: plans.starter.planCode })}
          />

          <PricingCard
            {...plans.pro}
            active
            isCurrentPlan={hasManagedSubscription && billingPlanCode === plans.pro.planCode}
            ctaOverride={ctaForPlan(plans.pro.planCode)}
            loading={loadingPlan === plans.pro.planCode}
            onSelect={() => startCheckout({ planCode: plans.pro.planCode })}
          />

          <PricingCard
            {...plans.elite}
            active
            isCurrentPlan={hasManagedSubscription && billingPlanCode === plans.elite.planCode}
            ctaOverride={ctaForPlan(plans.elite.planCode)}
            loading={loadingPlan === plans.elite.planCode}
            onSelect={() => startCheckout({ planCode: plans.elite.planCode })}
          />
        </section>

        <section className="pricing-mobile-only" aria-label="Pricing plans (mobile)">
          <PricingCard
            {...plans.starter}
            active={false}
            single
            isCurrentPlan={hasManagedSubscription && billingPlanCode === plans.starter.planCode}
            ctaOverride={ctaForPlan(plans.starter.planCode)}
            loading={loadingPlan === plans.starter.planCode}
            onSelect={() => startCheckout({ planCode: plans.starter.planCode })}
          />
          <PricingCard
            {...plans.pro}
            active
            single
            isCurrentPlan={hasManagedSubscription && billingPlanCode === plans.pro.planCode}
            ctaOverride={ctaForPlan(plans.pro.planCode)}
            loading={loadingPlan === plans.pro.planCode}
            onSelect={() => startCheckout({ planCode: plans.pro.planCode })}
          />
          <PricingCard
            {...plans.elite}
            active
            single
            isCurrentPlan={hasManagedSubscription && billingPlanCode === plans.elite.planCode}
            ctaOverride={ctaForPlan(plans.elite.planCode)}
            loading={loadingPlan === plans.elite.planCode}
            onSelect={() => startCheckout({ planCode: plans.elite.planCode })}
          />
        </section>
      </main>
    </div>
  );
}

function PricingCard({
  title,
  subtitle,
  badge,
  price,
  cadence,
  items,
  cta,
  planCode,
  active,
  single,
  loading,
  isCurrentPlan,
  ctaOverride,
  onSelect,
}) {
  const buttonLabel = isCurrentPlan ? "Current plan" : (ctaOverride || cta);
  return (
    <article className={`pricing-card ${active ? "is-active" : ""} ${single ? "is-single" : ""}`}>
      <div className="pricing-card-top">
        <div className="pricing-card-title-row">
          <div className="pricing-card-title-block">
            <p className="pricing-card-title">{title}</p>
            <p className="pricing-card-subtitle">
              {typeof subtitle === "string" ? (
                subtitle
              ) : (
                <>
                  <span className="pricing-subtitle-badge">{subtitle.badge}</span>
                  {subtitle.suffix}
                </>
              )}
            </p>
          </div>
          {badge ? <span className="pricing-badge">{badge}</span> : null}
        </div>

        <div className="pricing-price-row">
          <span className="pricing-price">{price}</span>
          <span className="pricing-cadence">{cadence}</span>
        </div>
      </div>

      <div className="pricing-actions">
        <button className="pricing-primary-btn" onClick={onSelect} disabled={loading || isCurrentPlan}>
          {loading ? "Redirecting…" : buttonLabel} <ArrowUpRight />
        </button>
      </div>

      <p className="pricing-included-title">What&apos;s included</p>

      <ul className="pricing-list">
        {items.map((t) => (
          <li key={typeof t === "string" ? t : `${t.strong}-${t.suffix || ""}`} className="pricing-list-item">
            <span className="pricing-check-icon" aria-hidden="true">✓</span>
            <span className="pricing-list-text">
              {typeof t === "string" ? (
                t
              ) : (
                <>
                  {t.prefix || ""}
                  <strong>{t.strong}</strong>
                  {t.suffix || ""}
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}
